/**
 * Shared Firebase App Check helpers for the web app and Chrome extension.
 */

export const shouldInitializeAppCheck = ({appCheckSiteKey, shouldUseEmulator}) => {
	const key = String(appCheckSiteKey ?? '').trim();
	return Boolean(key) && !shouldUseEmulator;
};

/**
 * @param {object} app - Firebase app instance.
 * @param {object} [options]
 * @param {string}   [options.appCheckSiteKey]
 * @param {boolean}  [options.shouldUseEmulator]
 * @param {function} [options.onError]      Called with (message, error) on SDK init failure.
 * @param {function} [options.onTokenError] Called with (error) on token exchange failure
 *                                          (e.g. appCheck/initial-throttle, HTTP 403).
 * @returns {Promise<boolean>}
 */
export const initializeFirebaseAppCheck = async (app, {appCheckSiteKey, shouldUseEmulator, onError, onTokenError} = {}) => {
	if (!shouldInitializeAppCheck({appCheckSiteKey, shouldUseEmulator})) {
		return false;
	}

	try {
		const {initializeAppCheck, ReCaptchaEnterpriseProvider, onTokenChanged} = await import('firebase/app-check');
		const appCheck = initializeAppCheck(app, {
			provider: new ReCaptchaEnterpriseProvider(String(appCheckSiteKey).trim()),
			isTokenAutoRefreshEnabled: true
		});
		if (onTokenError) {
			// onTokenChanged fires on every token refresh; the error callback catches
			// token exchange failures including appCheck/initial-throttle and HTTP 403s.
			onTokenChanged(appCheck, () => {}, onTokenError);
		}
		return true;
	} catch (error) {
		const message = error?.message ?? String(error);
		if (onError) {
			onError(message, error);
		}
		return false;
	}
};
